import React, { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { CompetitionAPI } from "../../../services/api";
import { API_URL } from "../../../config/api";
import DashboardLayout from "../../../components/layouts/DashboardLayout";
import Countdown from "../../../components/common/Countdown";

// Category IDs
const MARKETERS_CATEGORY_ID = 3;
const FILM_CATEGORY_ID = 2;
const DIGITAL_CATEGORY_ID = 1;

// Language translations - for future multilanguage support
const translations = {
  en: {
    // Navigation
    backToCompetitions: "Back to Competitions",
    loading: "Loading competition...",
    notFoundError: "Competition not found or you don't have access.",
    // Phase labels
    viewBrief: "View Brief",
    submitEntry: "Submit Entry",
    // Phase status
    open: "Open",
    waitingForBrief: "Waiting for brief",
    closed: "Closed",
    notConfigured: "Not configured",
    submitted: "Submitted",
    waitingToOpen: "Waiting to open",
    // Countdown labels
    briefOpensIn: "Brief will be available in:",
    briefTimeLeft: "Brief access time left:",
    submissionOpensIn: "Submission opens in:",
    submissionTimeLeft: "Submission time left:",
    // Time labels
    start: "Start:",
    end: "End:",
    // Status messages
    briefTimeExpired: "Brief access time expired",
    briefNotAvailable: "Brief is not available yet",
    submittedSuccess: "Submitted successfully",
    submissionTimeExpired: "Submission time expired",
    submissionNotOpen: "Submission is not open yet",
    scheduleNotConfigured: "Schedule not configured",
    // Download button
    downloadBrief: "Download Brief",
    // Submission form
    submitEntryTitle: "Submit Your Entry",
    requiredFilesFor: "Required files for",
    thumbnailImage: "Thumbnail Image",
    pdfFile: "PDF File",
    videoFile: "Video File",
    consentForm: "Consent Form",
    bigIdea: "Big Idea",
    maxSlides: "max 10 slides",
    oneSlide: "1 slide",
    maxWords: "max 10 words",
    clickToSelect: "Click to select file",
    orDragDrop: "or drag and drop here",
    uploaded: "Uploaded",
    clickToChange: "Click to change file",
    uploading: "Uploading...",
    save: "Save",
    saving: "Saving...",
    words: "words",
    submitEntryBtn: "Submit Entry",
    submitting: "Submitting...",
    // Confirmation
    confirmSubmitMarketer:
      "Are you sure you want to submit? Make sure you have uploaded the PDF file (max 10 slides) and thumbnail.",
    confirmSubmitDigital:
      "Are you sure you want to submit? Make sure you have uploaded the PDF file and thumbnail.",
    confirmSubmitFilm:
      "Are you sure you want to submit? Make sure you have uploaded all required files: PDF, Thumbnail, and Video.",
    submissionSuccess: "Submission successful!",
    // Success/Error messages
    successTitle: "Submission Successful!",
    successMessage: "Your entry has been submitted at",
    resultsNotice: "Results will be announced after judging is complete.",
    expiredTitle: "Submission Deadline Passed",
    expiredMessage: "Unfortunately, the submission deadline has passed.",
    cannotSubmit: "You cannot submit for this competition.",
    // Consent form post-submission upload
    consentFormUpdateTitle: "Upload Consent Form",
    consentFormUpdateDesc:
      "You have not uploaded the Consent Form yet. You can still upload it now.",
    consentFormUploadBtn: "Upload Consent Form",
    consentFormUploading: "Uploading...",
    consentFormUploadSuccess: "Consent Form uploaded successfully!",
    // Sidebar
    submissionGuideTitle: "Submission Guide",
    guide1: "1. Download the brief when access opens.",
    guide2: "2. Prepare your entry according to requirements.",
    guide3: "3. Upload files in the correct format.",
    guide4: "4. Fill in Big Idea (max 10 words).",
    guide5: '5. Review carefully and click "Submit Entry".',
    guideWarning: "Note: After submission, you cannot edit!",
    requirementsFor: "Requirements for",
    thumbnail: "Thumbnail",
    pdf: "PDF",
    video: "Video",
    bigIdeaReq: "Big Idea: Required, max 10 words",
  },
  vi: {
    // Navigation
    backToCompetitions: "Quay lại Cuộc thi",
    loading: "Đang tải cuộc thi...",
    notFoundError: "Không tìm thấy cuộc thi hoặc bạn không có quyền truy cập.",
    // Phase labels
    viewBrief: "Xem Đề Thi",
    submitEntry: "Nộp Bài Thi",
    // Phase status
    open: "Đang mở",
    waitingForBrief: "Chờ ra đề",
    closed: "Đã đóng",
    notConfigured: "Chưa cấu hình",
    submitted: "Đã nộp bài",
    waitingToOpen: "Chờ mở",
    // Countdown labels
    briefOpensIn: "Đề thi sẽ được mở sau:",
    briefTimeLeft: "Thời gian xem đề còn:",
    submissionOpensIn: "Cổng nộp bài sẽ mở sau:",
    submissionTimeLeft: "Thời gian nộp bài còn:",
    // Time labels
    start: "Bắt đầu:",
    end: "Kết thúc:",
    // Status messages
    briefTimeExpired: "Đã hết thời gian xem đề",
    briefNotAvailable: "Đề thi chưa được mở",
    submittedSuccess: "Đã nộp bài thành công",
    submissionTimeExpired: "Đã hết thời gian nộp bài",
    submissionNotOpen: "Cổng nộp bài chưa mở",
    scheduleNotConfigured: "Chưa cấu hình lịch",
    // Download button
    downloadBrief: "Tải Đề Thi",
    // Submission form
    submitEntryTitle: "Nộp Bài Dự Thi",
    requiredFilesFor: "Các file cần nộp cho",
    thumbnailImage: "Thumbnail Image",
    pdfFile: "PDF File",
    videoFile: "Video File",
    consentForm: "Consent Form",
    bigIdea: "Big Idea",
    maxSlides: "tối đa 10 slides",
    oneSlide: "1 slide",
    maxWords: "tối đa 10 từ",
    clickToSelect: "Click để chọn file",
    orDragDrop: "hoặc kéo thả file vào đây",
    uploaded: "Đã upload",
    clickToChange: "Click để thay đổi file",
    uploading: "Đang upload...",
    save: "Lưu",
    saving: "Đang lưu...",
    words: "từ",
    submitEntryBtn: "Nộp Bài Dự Thi",
    submitting: "Đang nộp...",
    // Confirmation
    confirmSubmitMarketer:
      "Bạn có chắc chắn muốn nộp bài? Hãy đảm bảo đã upload file PDF (tối đa 10 slides) và thumbnail.",
    confirmSubmitDigital:
      "Bạn có chắc chắn muốn nộp bài? Hãy đảm bảo đã upload file PDF và thumbnail.",
    confirmSubmitFilm:
      "Bạn có chắc chắn muốn nộp bài? Hãy đảm bảo đã upload đầy đủ các file bắt buộc: PDF, Thumbnail và Video.",
    submissionSuccess: "Nộp bài thành công!",
    // Success/Error messages
    successTitle: "Nộp Bài Thành Công!",
    successMessage: "Bài dự thi của bạn đã được nộp vào lúc",
    resultsNotice: "Kết quả sẽ được thông báo sau khi hoàn thành chấm điểm.",
    expiredTitle: "Đã Hết Hạn Nộp Bài",
    expiredMessage: "Rất tiếc, thời gian nộp bài đã kết thúc.",
    cannotSubmit: "Bạn không thể nộp bài cho cuộc thi này.",
    // Consent form post-submission upload
    consentFormUpdateTitle: "Upload Consent Form",
    consentFormUpdateDesc:
      "Bạn chưa upload Consent Form. Bạn vẫn có thể upload file này ngay bây giờ.",
    consentFormUploadBtn: "Upload Consent Form",
    consentFormUploading: "Đang upload...",
    consentFormUploadSuccess: "Upload Consent Form thành công!",
    // Sidebar
    submissionGuideTitle: "Hướng dẫn nộp bài",
    guide1: "1. Tải đề thi khi cổng xem đề được mở.",
    guide2: "2. Chuẩn bị bài dự thi theo yêu cầu của đề.",
    guide3: "3. Upload các file theo đúng định dạng yêu cầu.",
    guide4: "4. Điền Big Idea (tối đa 10 từ).",
    guide5: '5. Kiểm tra kỹ và bấm "Nộp Bài Dự Thi".',
    guideWarning: "Lưu ý: Sau khi nộp bài, bạn không thể chỉnh sửa!",
    requirementsFor: "Yêu cầu cho",
    thumbnail: "Thumbnail",
    pdf: "PDF",
    video: "Video",
    bigIdeaReq: "Big Idea: Bắt buộc, tối đa 10 từ",
  },
};

// Current language - change to 'vi' for Vietnamese
const currentLang: "en" | "vi" = "en";
const t = translations[currentLang];

// Types
interface PhaseInfo {
  status: "waiting" | "active" | "closed" | "not_configured";
  is_active: boolean;
  start_time: string | null;
  end_time: string | null;
  countdown_to: string | null;
  has_brief?: boolean;
  brief_url?: string | null;
}

interface TeamStatus {
  team: {
    id: number;
    team_id: string;
    team_name: string;
    category: { id: number; name: string };
    league: { id: number; name: string };
  };
  is_eligible: boolean;
  eligibility_reasons: string[];
  contest_access: PhaseInfo | null;
  submission_window: PhaseInfo | null;
  submission: {
    id: number;
    status: string;
    has_thumbnail: boolean;
    has_pdf: boolean;
    has_video: boolean;
    has_consent: boolean;
    thumbnail_url: string | null;
    pdf_url: string | null;
    video_url: string | null;
    consent_url: string | null;
    big_idea: string | null;
    submitted_at: string | null;
    passed_round?: number;
    round1_feedback?: { jury_name: string; comment: string }[];
  } | null;
  required_files: string[];
  validation_rules: any;
}

// File Upload Box Component
interface FileUploadBoxProps {
  label: string;
  description?: string;
  accept: string;
  isUploaded: boolean; // File already uploaded to server (from previous submission)
  isSelected: boolean; // File selected locally (not yet uploaded)
  selectedFileName?: string; // Name of selected file
  isUploading: boolean; // Currently uploading this file
  uploadProgress?: number;
  onSelect: (file: File) => void;
  onRemove: () => void;
  icon: React.ReactNode;
  disabled?: boolean;
  optional?: boolean; // If true, field is not required
}

const FileUploadBox: React.FC<FileUploadBoxProps> = ({
  label,
  description,
  accept,
  isUploaded,
  isSelected,
  selectedFileName,
  isUploading,
  uploadProgress = 0,
  onSelect,
  onRemove,
  icon,
  disabled = false,
  optional = false,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);

  const handleClick = () => {
    if (!disabled && !isUploading) {
      inputRef.current?.click();
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      onSelect(file);
      e.target.value = "";
    }
  };

  const handleRemove = (e: React.MouseEvent) => {
    e.stopPropagation();
    onRemove();
  };

  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-2">
        {label} {!optional && "*"}
        {description && (
          <span className="font-normal text-gray-500 ml-1">{description}</span>
        )}
      </label>
      <div
        className={`border-2 border-dashed rounded-lg p-6 text-center transition-all ${
          isUploading
            ? "border-blue-300 bg-blue-50 cursor-not-allowed"
            : isSelected
              ? "border-yellow-400 bg-yellow-50 cursor-pointer hover:bg-yellow-100"
              : isUploaded
                ? "border-green-300 bg-green-50 cursor-pointer hover:bg-green-100"
                : disabled
                  ? "border-gray-200 bg-gray-100 cursor-not-allowed"
                  : "border-gray-300 cursor-pointer hover:border-gray-400 hover:bg-gray-50"
        }`}
        onClick={handleClick}
      >
        {isUploading ? (
          <div className="text-blue-600">
            <svg
              className="w-8 h-8 mx-auto animate-spin"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </svg>
            <p className="mt-2 text-sm font-medium">{t.uploading}</p>
            <div className="mt-3 w-full bg-blue-200 rounded-full h-2">
              <div
                className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                style={{ width: `${uploadProgress}%` }}
              />
            </div>
            <p className="mt-1 text-xs text-blue-500">{uploadProgress}%</p>
          </div>
        ) : isSelected ? (
          <div className="text-yellow-700">
            <svg
              className="w-8 h-8 mx-auto text-yellow-500"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
              />
            </svg>
            <p
              className="mt-2 text-sm font-medium truncate px-2"
              title={selectedFileName}
            >
              {selectedFileName}
            </p>
            <p className="mt-1 text-xs text-yellow-600">Ready to upload</p>
            <button
              type="button"
              onClick={handleRemove}
              className="mt-2 text-xs text-red-500 hover:text-red-700 underline"
            >
              Remove
            </button>
          </div>
        ) : isUploaded ? (
          <div className="text-green-600">
            <svg
              className="w-8 h-8 mx-auto"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M5 13l4 4L19 7"
              />
            </svg>
            <p className="mt-2 text-sm font-medium">{t.uploaded}</p>
            <p className="mt-1 text-xs text-green-500">{t.clickToChange}</p>
          </div>
        ) : (
          <div className={disabled ? "text-gray-400" : "text-gray-500"}>
            <div className="w-8 h-8 mx-auto">{icon}</div>
            <p className="mt-2 text-sm font-medium">{t.clickToSelect}</p>
            <p className="mt-1 text-xs text-gray-400">{t.orDragDrop}</p>
          </div>
        )}
        <input
          ref={inputRef}
          type="file"
          accept={accept}
          onChange={handleChange}
          disabled={isUploading || disabled}
          className="hidden"
        />
      </div>
    </div>
  );
};

// Type for selected files (not yet uploaded)
interface SelectedFiles {
  thumbnail?: File;
  pdf_file?: File;
  video_file?: File;
  consent_form?: File;
}

const CompetitionDetailPage: React.FC = () => {
  const { teamId } = useParams<{ teamId: string }>();
  const navigate = useNavigate();

  const [status, setStatus] = useState<TeamStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [uploadingFile, setUploadingFile] = useState<string | null>(null);
  const [uploadProgress, setUploadProgress] = useState<number>(0);
  const [bigIdea, setBigIdea] = useState("");
  const [refreshKey, setRefreshKey] = useState(0);

  // Store selected files locally (not uploaded yet)
  const [selectedFiles, setSelectedFiles] = useState<SelectedFiles>({});

  const fetchStatus = useCallback(async () => {
    if (!teamId) return;

    try {
      setLoading(true);
      setError(null);
      const response = await CompetitionAPI.getTeamStatus(parseInt(teamId));

      if (response.success && response.data) {
        setStatus(response.data);
        setBigIdea(response.data.submission?.big_idea || "");
      } else {
        setError(response.message || "Failed to load competition status");
      }
    } catch (err: any) {
      console.error("Error fetching status:", err);
      setError(err.message || "Failed to load competition status");
    } finally {
      setLoading(false);
    }
  }, [teamId]);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus, refreshKey]);

  const handleRefresh = () => {
    setRefreshKey((prev) => prev + 1);
  };

  // Select file (store locally, not upload yet)
  const handleFileSelect = (fileType: keyof SelectedFiles, file: File) => {
    setSelectedFiles((prev) => ({
      ...prev,
      [fileType]: file,
    }));
  };

  // Remove selected file
  const handleFileRemove = (fileType: keyof SelectedFiles) => {
    setSelectedFiles((prev) => {
      const newFiles = { ...prev };
      delete newFiles[fileType];
      return newFiles;
    });
  };

  // Upload a single file with progress tracking
  const uploadSingleFile = (fileType: string, file: File): Promise<boolean> => {
    return new Promise((resolve) => {
      const formData = new FormData();
      formData.append("team_id", teamId!);
      formData.append("file_type", fileType);
      formData.append("file", file);

      const token = localStorage.getItem("token");
      const xhr = new XMLHttpRequest();

      xhr.upload.addEventListener("progress", (event) => {
        if (event.lengthComputable) {
          const percentComplete = Math.round(
            (event.loaded / event.total) * 100,
          );
          setUploadProgress(percentComplete);
        }
      });

      xhr.addEventListener("load", () => {
        try {
          const response = JSON.parse(xhr.responseText);
          if (xhr.status >= 200 && xhr.status < 300 && response.success) {
            resolve(true);
          } else {
            setError(response.message || `Upload ${fileType} failed`);
            resolve(false);
          }
        } catch (err) {
          setError(`Upload ${fileType} failed - Invalid response`);
          resolve(false);
        }
      });

      xhr.addEventListener("error", () => {
        setError(`Upload ${fileType} failed - Network error`);
        resolve(false);
      });

      xhr.addEventListener("timeout", () => {
        setError(
          `Upload ${fileType} failed - Request timeout. Please try again.`,
        );
        resolve(false);
      });

      xhr.open("POST", API_URL.COMPETITIONS.UPLOAD);
      xhr.setRequestHeader("Authorization", `Bearer ${token}`);
      xhr.setRequestHeader("Accept", "application/json");
      xhr.timeout = 600000; // 10 minutes for large files
      xhr.send(formData);
    });
  };

  const handleSubmit = async () => {
    if (!teamId || !status) return;

    // Use category ID to determine confirmation message
    const categoryId = status.team.category.id;
    let confirmMsg = t.confirmSubmitFilm; // Default for Film
    if (categoryId === MARKETERS_CATEGORY_ID) {
      confirmMsg = t.confirmSubmitMarketer;
    } else if (categoryId === DIGITAL_CATEGORY_ID) {
      confirmMsg = t.confirmSubmitDigital;
    }

    if (!window.confirm(confirmMsg)) return;

    try {
      setSubmitting(true);
      setError(null);

      // Define file upload order
      const fileTypes: (keyof SelectedFiles)[] = [
        "thumbnail",
        "pdf_file",
        "video_file",
        "consent_form",
      ];

      // Upload all selected files sequentially
      for (const fileType of fileTypes) {
        const file = selectedFiles[fileType];
        if (file) {
          setUploadingFile(fileType);
          setUploadProgress(0);

          const success = await uploadSingleFile(fileType, file);
          if (!success) {
            setSubmitting(false);
            setUploadingFile(null);
            return; // Stop if any upload fails
          }
        }
      }

      setUploadingFile(null);
      setUploadProgress(0);

      // Save Big Idea if it has content
      if (bigIdea.trim()) {
        await CompetitionAPI.updateBigIdea({
          team_id: parseInt(teamId),
          big_idea: bigIdea,
        });
      }

      // Then submit the entry
      const response = await CompetitionAPI.submitEntry(parseInt(teamId));

      if (response.success) {
        // Clear selected files after successful submit
        setSelectedFiles({});
        await fetchStatus();
        alert(t.submissionSuccess);
      } else {
        setError(response.message || "Submission failed");
      }
    } catch (err: any) {
      console.error("Submit error:", err);
      setError(err.message || "Submission failed");
    } finally {
      setSubmitting(false);
      setUploadingFile(null);
      setUploadProgress(0);
    }
  };

  const handleConsentUpload = async () => {
    const file = selectedFiles.consent_form;
    if (!file || !teamId) return;

    try {
      setSubmitting(true);
      setError(null);
      setUploadingFile("consent_form");
      setUploadProgress(0);

      const success = await uploadSingleFile("consent_form", file);
      if (success) {
        setSelectedFiles((prev) => {
          const next = { ...prev };
          delete next.consent_form;
          return next;
        });
        await fetchStatus();
        alert(t.consentFormUploadSuccess);
      }
    } catch (err: any) {
      setError(err.message || "Upload failed");
    } finally {
      setSubmitting(false);
      setUploadingFile(null);
      setUploadProgress(0);
    }
  };

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  // Use category ID instead of name for better accuracy
  const isMarketerCategory = status?.team.category.id === MARKETERS_CATEGORY_ID;
  const isFilmCategory = status?.team.category.id === FILM_CATEGORY_ID;
  const isDigitalCategory = status?.team.category.id === DIGITAL_CATEGORY_ID;

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-800 mx-auto mb-4"></div>
            <p className="text-gray-500">{t.loading}</p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  if (!status) {
    return (
      <DashboardLayout>
        <div className="max-w-4xl mx-auto px-4 py-8">
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
            {t.notFoundError}
          </div>
          <button
            onClick={() => navigate("/dashboard/competitions")}
            className="mt-4 text-gray-600 hover:underline"
          >
            &larr; {t.backToCompetitions}
          </button>
        </div>
      </DashboardLayout>
    );
  }

  // Icons for file types
  const ImageIcon = (
    <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
      />
    </svg>
  );

  const PdfIcon = (
    <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
      />
    </svg>
  );

  const VideoIcon = (
    <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"
      />
    </svg>
  );

  const ConsentIcon = (
    <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
      />
    </svg>
  );

  return (
    <DashboardLayout>
      <div className="flex flex-col lg:flex-row gap-8 bg-[#EDEDED] rounded-lg p-4 sm:p-6 lg:p-8 shadow-sm">
        {/* Main Content */}
        <div className="flex-1">
          {/* Header */}
          <div className="mb-6">
            <button
              onClick={() => navigate("/dashboard/competitions")}
              className="text-gray-500 hover:text-gray-700 mb-4 inline-flex items-center text-sm"
            >
              <svg
                className="w-4 h-4 mr-1"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 19l-7-7 7-7"
                />
              </svg>
              {t.backToCompetitions}
            </button>
            <h1 className="text-2xl font-bold text-gray-900">
              {status.team.team_name}
            </h1>
            <p className="mt-1 text-sm text-gray-600">
              {status.team.category.name} - {status.team.league.name}
            </p>
          </div>

          {error && (
            <div className="mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-center justify-between">
              <span>{error}</span>
              <button
                onClick={() => setError(null)}
                className="text-red-500 hover:text-red-700 text-xl"
              >
                &times;
              </button>
            </div>
          )}

          {/* Phase Status Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            {/* Contest Access (Brief) Phase */}
            <div
              className={`bg-white border-2 border-[#262423] rounded-lg p-4 sm:p-6 shadow-sm`}
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-base font-semibold text-gray-900 flex items-center">
                  <svg
                    className="w-5 h-5 mr-2 text-blue-600"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                    />
                  </svg>
                  {t.viewBrief}
                </h3>
                <span
                  className={`px-2 py-1 rounded-full text-xs font-medium ${
                    status.contest_access?.status === "active"
                      ? "bg-green-100 text-green-800"
                      : status.contest_access?.status === "waiting"
                        ? "bg-yellow-100 text-yellow-800"
                        : status.contest_access?.status === "closed"
                          ? "bg-red-100 text-red-800"
                          : "bg-gray-100 text-gray-800"
                  }`}
                >
                  {status.contest_access?.status === "active"
                    ? t.open
                    : status.contest_access?.status === "waiting"
                      ? t.waitingForBrief
                      : status.contest_access?.status === "closed"
                        ? t.closed
                        : t.notConfigured}
                </span>
              </div>

              {status.contest_access ? (
                <>
                  {/* Countdown */}
                  {status.contest_access.countdown_to && (
                    <div className="mb-4 p-3 bg-gray-50 rounded-lg">
                      <p className="text-xs text-gray-600 mb-2 text-center">
                        {status.contest_access.status === "waiting"
                          ? t.briefOpensIn
                          : t.briefTimeLeft}
                      </p>
                      <div className="flex justify-center">
                        <Countdown
                          targetDate={status.contest_access.countdown_to}
                          size="sm"
                          onComplete={handleRefresh}
                        />
                      </div>
                    </div>
                  )}

                  {/* Time Range */}
                  <div className="text-xs text-gray-500 space-y-1 mb-4">
                    <p>
                      <span className="font-medium">{t.start}</span>{" "}
                      {formatDate(status.contest_access.start_time)}
                    </p>
                    <p>
                      <span className="font-medium">{t.end}</span>{" "}
                      {formatDate(status.contest_access.end_time)}
                    </p>
                  </div>

                  {/* Download Brief Button */}
                  {status.contest_access.status === "active" &&
                    status.contest_access.has_brief && (
                      <a
                        href={status.contest_access.brief_url || "#"}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="w-full inline-flex items-center justify-center px-4 py-2 bg-gradient-to-b from-[#403634] to-[#1E1E1E] text-white font-medium rounded-lg hover:opacity-90 transition-opacity text-sm"
                      >
                        <svg
                          className="w-4 h-4 mr-2"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                          />
                        </svg>
                        {t.downloadBrief}
                      </a>
                    )}

                  {status.contest_access.status === "closed" && (
                    <div className="p-2 bg-red-50 rounded-lg text-center">
                      <p className="text-red-600 font-medium text-xs">
                        {t.briefTimeExpired}
                      </p>
                    </div>
                  )}

                  {status.contest_access.status === "waiting" && (
                    <div className="p-2 bg-yellow-50 rounded-lg text-center">
                      <p className="text-yellow-700 font-medium text-xs">
                        {t.briefNotAvailable}
                      </p>
                    </div>
                  )}
                </>
              ) : (
                <p className="text-gray-400 text-sm">
                  {t.scheduleNotConfigured}
                </p>
              )}
            </div>

            {/* Submission Window Phase */}
            <div
              className={`bg-white border-2 border-[#262423] rounded-lg p-4 sm:p-6 shadow-sm`}
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-base font-semibold text-gray-900 flex items-center">
                  <svg
                    className="w-5 h-5 mr-2 text-green-600"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12"
                    />
                  </svg>
                  {t.submitEntry}
                </h3>
                <span
                  className={`px-2 py-1 rounded-full text-xs font-medium ${
                    status.submission?.status === "submitted"
                      ? "bg-green-100 text-green-800"
                      : status.submission_window?.status === "active"
                        ? "bg-green-100 text-green-800"
                        : status.submission_window?.status === "waiting"
                          ? "bg-yellow-100 text-yellow-800"
                          : status.submission_window?.status === "closed"
                            ? "bg-red-100 text-red-800"
                            : "bg-gray-100 text-gray-800"
                  }`}
                >
                  {status.submission?.status === "submitted"
                    ? t.submitted
                    : status.submission_window?.status === "active"
                      ? t.open
                      : status.submission_window?.status === "waiting"
                        ? t.waitingToOpen
                        : status.submission_window?.status === "closed"
                          ? t.closed
                          : t.notConfigured}
                </span>
              </div>

              {status.submission_window ? (
                <>
                  {/* Countdown */}
                  {status.submission_window.countdown_to &&
                    status.submission?.status !== "submitted" && (
                      <div className="mb-4 p-3 bg-gray-50 rounded-lg">
                        <p className="text-xs text-gray-600 mb-2 text-center">
                          {status.submission_window.status === "waiting"
                            ? t.submissionOpensIn
                            : t.submissionTimeLeft}
                        </p>
                        <div className="flex justify-center">
                          <Countdown
                            targetDate={status.submission_window.countdown_to}
                            size="sm"
                            onComplete={handleRefresh}
                          />
                        </div>
                      </div>
                    )}

                  {/* Time Range */}
                  <div className="text-xs text-gray-500 space-y-1 mb-4">
                    <p>
                      <span className="font-medium">{t.start}</span>{" "}
                      {formatDate(status.submission_window.start_time)}
                    </p>
                    <p>
                      <span className="font-medium">{t.end}</span>{" "}
                      {formatDate(status.submission_window.end_time)}
                    </p>
                  </div>

                  {/* Submission Status Messages */}
                  {status.submission?.status === "submitted" && (
                    <div className="p-2 bg-green-50 rounded-lg text-center">
                      <p className="text-green-600 font-medium text-xs">
                        {t.submittedSuccess}
                      </p>
                      <p className="text-[10px] text-green-500 mt-0.5">
                        {formatDate(status.submission.submitted_at)}
                      </p>
                    </div>
                  )}

                  {status.submission_window.status === "closed" &&
                    status.submission?.status !== "submitted" && (
                      <div className="p-2 bg-red-50 rounded-lg text-center">
                        <p className="text-red-600 font-medium text-xs">
                          {t.submissionTimeExpired}
                        </p>
                      </div>
                    )}

                  {status.submission_window.status === "waiting" && (
                    <div className="p-2 bg-yellow-50 rounded-lg text-center">
                      <p className="text-yellow-700 font-medium text-xs">
                        {t.submissionNotOpen}
                      </p>
                    </div>
                  )}
                </>
              ) : (
                <p className="text-gray-400 text-sm">
                  {t.scheduleNotConfigured}
                </p>
              )}
            </div>
          </div>

          {/* Submission Form - Only show when submission window is active and not submitted */}
          {status.is_eligible &&
            status.submission_window?.is_active &&
            status.submission?.status !== "submitted" && (
              <div className="bg-white border-2 border-[#262423] rounded-lg p-4 sm:p-6 shadow-sm mb-6">
                <h2 className="text-lg font-bold text-gray-900 mb-4">
                  {t.submitEntryTitle}
                </h2>

                {/* Required Files Info */}
                <div className="mb-6 p-4 bg-blue-50 rounded-lg">
                  <h4 className="font-medium text-blue-800 mb-2 text-sm">
                    {t.requiredFilesFor} {status.team.category.name}:
                  </h4>
                  <ul className="text-xs text-blue-700 list-disc list-inside space-y-1">
                    <li>
                      {isFilmCategory
                        ? "Thumbnail is the screen grab/ screen capture of the 1-slide summary: PNG or JPG, max. 2MB, required"
                        : isMarketerCategory
                          ? "Thumbnail is the screen grab/ screen capture of the executive summary slide: PNG or JPG, max. 2MB, required"
                          : "Thumbnail is the screen grab/ screen capture of the 1 slide: PNG or JPG, max. 2MB, required"}
                    </li>
                    {isFilmCategory && (
                      <li>Video file: MP4 or MOV, max. 100MB, required</li>
                    )}
                    <li>
                      PDF file:{" "}
                      {isMarketerCategory
                        ? t.maxSlides + ", "
                        : t.oneSlide + ", "}
                      max. 10MB, required
                    </li>
                    {isFilmCategory && (
                      <li>
                        Consent form: PDF, max. 10MB, optional (not required if
                        no actors appear in the video)
                      </li>
                    )}
                    <li>
                      Big Idea: English or Vietnamese, max. 10 words, required
                    </li>
                  </ul>
                </div>

                {/* File Upload Section */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                  {/* Thumbnail */}
                  <FileUploadBox
                    label={t.thumbnailImage}
                    accept="image/jpeg,image/png,image/jpg"
                    isUploaded={status.submission?.has_thumbnail || false}
                    isSelected={!!selectedFiles.thumbnail}
                    selectedFileName={selectedFiles.thumbnail?.name}
                    isUploading={uploadingFile === "thumbnail"}
                    uploadProgress={
                      uploadingFile === "thumbnail" ? uploadProgress : 0
                    }
                    onSelect={(file) => handleFileSelect("thumbnail", file)}
                    onRemove={() => handleFileRemove("thumbnail")}
                    icon={ImageIcon}
                    disabled={submitting}
                  />

                  {/* PDF File */}
                  <FileUploadBox
                    label={t.pdfFile}
                    description={
                      isMarketerCategory
                        ? `(${t.maxSlides})`
                        : `(${t.oneSlide})`
                    }
                    accept="application/pdf"
                    isUploaded={status.submission?.has_pdf || false}
                    isSelected={!!selectedFiles.pdf_file}
                    selectedFileName={selectedFiles.pdf_file?.name}
                    isUploading={uploadingFile === "pdf_file"}
                    uploadProgress={
                      uploadingFile === "pdf_file" ? uploadProgress : 0
                    }
                    onSelect={(file) => handleFileSelect("pdf_file", file)}
                    onRemove={() => handleFileRemove("pdf_file")}
                    icon={PdfIcon}
                    disabled={submitting}
                  />

                  {/* Video File (Film only) */}
                  {isFilmCategory && (
                    <FileUploadBox
                      label={t.videoFile}
                      accept="video/mp4,video/quicktime"
                      isUploaded={status.submission?.has_video || false}
                      isSelected={!!selectedFiles.video_file}
                      selectedFileName={selectedFiles.video_file?.name}
                      isUploading={uploadingFile === "video_file"}
                      uploadProgress={
                        uploadingFile === "video_file" ? uploadProgress : 0
                      }
                      onSelect={(file) => handleFileSelect("video_file", file)}
                      onRemove={() => handleFileRemove("video_file")}
                      icon={VideoIcon}
                      disabled={submitting}
                    />
                  )}

                  {/* Consent Form (Film only) - Optional */}
                  {isFilmCategory && (
                    <FileUploadBox
                      label={t.consentForm}
                      accept="application/pdf"
                      isUploaded={status.submission?.has_consent || false}
                      isSelected={!!selectedFiles.consent_form}
                      selectedFileName={selectedFiles.consent_form?.name}
                      isUploading={uploadingFile === "consent_form"}
                      uploadProgress={
                        uploadingFile === "consent_form" ? uploadProgress : 0
                      }
                      onSelect={(file) =>
                        handleFileSelect("consent_form", file)
                      }
                      onRemove={() => handleFileRemove("consent_form")}
                      icon={ConsentIcon}
                      disabled={submitting}
                      optional={true}
                    />
                  )}
                </div>

                {/* Big Idea */}
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t.bigIdea} *{" "}
                    <span className="font-normal text-gray-500">
                      ({t.maxWords})
                    </span>
                  </label>
                  <textarea
                    value={bigIdea}
                    onChange={(e) => setBigIdea(e.target.value)}
                    maxLength={500}
                    rows={3}
                    className="w-full rounded-lg border-2 border-gray-300 shadow-sm focus:border-[#403634] focus:ring-[#403634] p-3 text-sm"
                    placeholder="Describe your big idea..."
                  />
                  <div className="mt-2">
                    <span className="text-xs text-gray-400">
                      {bigIdea.split(/\s+/).filter((w) => w).length}/10{" "}
                      {t.words}
                    </span>
                  </div>
                </div>

                {/* Submit Button */}
                <div className="flex justify-end">
                  <button
                    onClick={handleSubmit}
                    disabled={submitting}
                    className="inline-flex items-center px-6 py-3 border border-transparent text-sm font-medium rounded-lg shadow-sm text-white bg-gradient-to-b from-[#403634] to-[#1E1E1E] hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition-opacity"
                  >
                    {submitting ? (
                      <>
                        <svg
                          className="animate-spin -ml-1 mr-3 h-4 w-4 text-white"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          />
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                          />
                        </svg>
                        {t.submitting}
                      </>
                    ) : (
                      t.submitEntryBtn
                    )}
                  </button>
                </div>
              </div>
            )}

          {/* Already Submitted */}
          {status.submission?.status === "submitted" && (
            <div className="bg-green-50 border-2 border-green-200 rounded-lg p-8 text-center">
              <svg
                className="w-16 h-16 text-green-500 mx-auto"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <h3 className="mt-4 text-xl font-bold text-green-800">
                {t.successTitle}
              </h3>
              <p className="mt-2 text-green-600 text-sm">
                {t.successMessage} {formatDate(status.submission.submitted_at)}.
                <br />
                {t.resultsNotice}
              </p>
            </div>
          )}

          {/* Feedback Round 1 - only show for teams still in round 1 (passed_round = 0) */}
          {status.submission?.status === "submitted" &&
            status.submission?.passed_round === 0 &&
            status.submission?.round1_feedback &&
            status.submission.round1_feedback.length > 0 && (
              <div className="bg-white border-2 border-gray-200 rounded-lg p-6 text-left mt-5">
                <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <svg
                    className="w-5 h-5 text-purple-600"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
                    />
                  </svg>
                  Feedback Round 1
                </h3>
                <div className="divide-y divide-gray-100">
                  {status.submission.round1_feedback.map((fb, idx) => (
                    <div
                      key={idx}
                      className="grid grid-cols-[180px_1fr] gap-4 py-3 text-left"
                    >
                      <div className="text-sm font-semibold text-purple-700 break-words">
                        {fb.jury_name}
                      </div>
                      <div className="text-sm text-gray-800 whitespace-pre-line break-words">
                        {fb.comment}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

          {/* Consent Form Upload After Submission (Film only, only if not uploaded) */}
          {status.submission?.status === "submitted" &&
            isFilmCategory &&
            !status.submission?.has_consent && (
              <div className="bg-yellow-50 border-2 border-yellow-300 rounded-lg p-6">
                <div className="flex items-center gap-2 mb-3">
                  <svg
                    className="w-5 h-5 text-yellow-600 flex-shrink-0"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  <h3 className="text-base font-semibold text-yellow-800">
                    {t.consentFormUpdateTitle}
                  </h3>
                </div>
                <p className="text-sm text-yellow-700 mb-4">
                  {t.consentFormUpdateDesc}
                </p>
                <FileUploadBox
                  label={t.consentForm}
                  description="(Film only) - Optional"
                  accept=".pdf"
                  isUploaded={false}
                  isSelected={!!selectedFiles.consent_form}
                  selectedFileName={selectedFiles.consent_form?.name}
                  isUploading={uploadingFile === "consent_form"}
                  uploadProgress={
                    uploadingFile === "consent_form" ? uploadProgress : 0
                  }
                  onSelect={(file) => handleFileSelect("consent_form", file)}
                  onRemove={() => handleFileRemove("consent_form")}
                  icon={PdfIcon}
                  optional
                  disabled={submitting && uploadingFile !== "consent_form"}
                />
                {error && <p className="mt-2 text-sm text-red-600">{error}</p>}
                <button
                  onClick={handleConsentUpload}
                  disabled={!selectedFiles.consent_form || submitting}
                  className="mt-4 px-5 py-2 bg-yellow-600 text-white text-sm font-medium rounded-lg hover:bg-yellow-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {submitting && uploadingFile === "consent_form" ? (
                    <>
                      <svg
                        className="w-4 h-4 animate-spin"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        />
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        />
                      </svg>
                      {t.consentFormUploading}
                    </>
                  ) : (
                    t.consentFormUploadBtn
                  )}
                </button>
              </div>
            )}

          {/* Submission Closed - Not Submitted */}
          {status.submission_window?.status === "closed" &&
            status.submission?.status !== "submitted" && (
              <div className="bg-red-50 border-2 border-red-200 rounded-lg p-8 text-center">
                <svg
                  className="w-16 h-16 text-red-400 mx-auto"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                <h3 className="mt-4 text-xl font-bold text-red-800">
                  {t.expiredTitle}
                </h3>
                <p className="mt-2 text-red-600 text-sm">
                  {t.expiredMessage}
                  <br />
                  {t.cannotSubmit}
                </p>
              </div>
            )}
        </div>

        {/* Right Sidebar */}
        <div className="hidden lg:block w-80 space-y-4">
          <div className="bg-white rounded-lg p-6 shadow-sm">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              {t.submissionGuideTitle}
            </h3>
            <div className="space-y-3 text-xs text-gray-600">
              <p>{t.guide1}</p>
              <p>{t.guide2}</p>
              <p>{t.guide3}</p>
              <p>{t.guide4}</p>
              <p>{t.guide5}</p>
              <p className="text-red-500 font-medium">{t.guideWarning}</p>
            </div>
          </div>

          {/* Category Requirements */}
          {/* <div className="bg-white rounded-lg p-6 shadow-sm">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              {t.requirementsFor} {status.team.category.name}
            </h3>
            <div className="space-y-2 text-xs text-gray-600">
              {isMarketerCategory ? (
                <>
                  <p>• {t.thumbnail}: JPG/PNG, max 5MB</p>
                  <p>
                    • {t.pdf}: {t.maxSlides}, 50MB
                  </p>
                  <p>• {t.bigIdeaReq}</p>
                </>
              ) : isDigitalCategory ? (
                <>
                  <p>• {t.thumbnail}: JPG/PNG, max 5MB</p>
                  <p>
                    • {t.pdf}: {t.oneSlide}, max 50MB
                  </p>
                  <p>• {t.video}: MP4, max 500MB (Optional)</p>
                  <p>• {t.consentForm}: PDF, max 10MB (Optional)</p>
                  <p>• {t.bigIdeaReq}</p>
                </>
              ) : (
                <>
                  <p>• {t.thumbnail}: JPG/PNG, max 5MB</p>
                  <p>
                    • {t.pdf}: {t.oneSlide}, max 50MB
                  </p>
                  <p>• {t.video}: MP4, max 500MB</p>
                  <p>• {t.consentForm}: PDF, max 10MB (Optional)</p>
                  <p>• {t.bigIdeaReq}</p>
                </>
              )}
            </div>
          </div> */}
        </div>
      </div>
    </DashboardLayout>
  );
};

export default CompetitionDetailPage;
